import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import BottomNav from './components/BottomNav';
import { isPhase2Enabled } from './shared/roles';
import './App.css';

// Eagerly load only the landing page (first paint)
import LandingPage from './pages/LandingPage';
const Terms = lazy(() => import('./pages/Terms'));

// Lazy-load authenticated routes — they only parse/execute when navigated to
const Welcome    = lazy(() => import('./pages/Welcome'));
const Onboarding  = lazy(() => import('./pages/Onboarding'));
const Dashboard   = lazy(() => import('./pages/Dashboard'));
const WorkoutPage = lazy(() => import('./pages/WorkoutPlans'));
const ActiveSession = lazy(() => import('./pages/ActiveSession'));
const DietPage    = lazy(() => import('./pages/DietPlans'));
const ProgressPage = lazy(() => import('./pages/ProgressPage'));
const Profile     = lazy(() => import('./pages/Profile'));
const PaymentPage = lazy(() => import('./pages/PaymentPage'));

// Phase 2 — lazy-loaded, registered only when REACT_APP_ENABLE_PHASE2 is not "false"
const ClientPlanEditor = lazy(() => import('./pages/trainer/ClientPlanEditor'));
const TrainerDashboard = lazy(() => import('./pages/trainer/TrainerDashboard'));
const TrainerProfile = lazy(() => import('./pages/trainer/TrainerProfile'));
const ClientRoster = lazy(() => import('./pages/trainer/ClientRoster'));
const TrainerMealLibrary = lazy(() => import('./pages/trainer/MealLibrary'));
const TrainerExerciseLibrary = lazy(() => import('./pages/trainer/ExerciseLibrary'));
const GymDashboard = lazy(() => import('./pages/gym/GymDashboard'));
const GymProfile = lazy(() => import('./pages/gym/GymProfile'));
const GymTrainers = lazy(() => import('./pages/gym/GymTrainers'));
const GymClients = lazy(() => import('./pages/gym/GymClients'));
const AdminReviewQueue = lazy(() => import('./pages/admin/AdminReviewQueue'));
const AdminDirectory = lazy(() => import('./pages/admin/AdminDirectory'));
const AdminPricing = lazy(() => import('./pages/admin/AdminPricing'));
const MarketplaceExplore = lazy(() => import('./pages/marketplace/Explore'));
const MarketplaceGymProfile = lazy(() => import('./pages/marketplace/GymProfile'));
const MarketplaceTrainerProfile = lazy(() => import('./pages/marketplace/TrainerProfile'));
const BecomeVendor = lazy(() => import('./pages/vendor/BecomeVendor'));
const GymApplication = lazy(() => import('./pages/vendor/GymApplication'));
const TrainerApplication = lazy(() => import('./pages/vendor/TrainerApplication'));
const UnderReview = lazy(() => import('./pages/vendor/UnderReview'));

// Minimal dark-mode fallback while a chunk loads
function PageLoader() {
  return (
    <div style={{ minHeight: '100vh', background: '#0B0B0B', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 32, height: 32, border: '3px solid #262626', borderTopColor: '#C6A85B', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  const phase2 = isPhase2Enabled();
  const path = location.pathname;
  const hideNavExact = ['/','','/welcome','/onboarding','/payment','/payment/success','/session','/apply','/terms'];
  const isTrainerPlanEditor = /^\/trainer\/clients\/[^/]+\/plan/.test(path);
  const isMarketplaceDetail = /^\/marketplace\/(gym|trainer)\/[^/]+/.test(path);
  const isApplyFlow = path.startsWith('/apply/');
  const showBottomNav =
    !hideNavExact.includes(path) &&
    !isTrainerPlanEditor &&
    !isMarketplaceDetail &&
    !isApplyFlow;
  return (
    <>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/welcome" element={<ProtectedRoute><Welcome /></ProtectedRoute>} />
          <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/workout" element={<ProtectedRoute requireSubscription><WorkoutPage /></ProtectedRoute>} />
          <Route path="/session" element={<ProtectedRoute requireSubscription><ActiveSession /></ProtectedRoute>} />
          <Route path="/diet" element={<ProtectedRoute requireSubscription><DietPage /></ProtectedRoute>} />
          <Route path="/progress" element={<ProtectedRoute requireSubscription><ProgressPage /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/payment" element={<ProtectedRoute><PaymentPage /></ProtectedRoute>} />
          <Route path="/payment/success" element={<ProtectedRoute><PaymentPage /></ProtectedRoute>} />

          {phase2 && (
            <>
              <Route path="/trainer/dashboard" element={<ProtectedRoute><TrainerDashboard /></ProtectedRoute>} />
              <Route path="/trainer/profile" element={<ProtectedRoute><TrainerProfile /></ProtectedRoute>} />
              <Route path="/trainer/clients" element={<ProtectedRoute><ClientRoster /></ProtectedRoute>} />
              <Route path="/trainer/meals" element={<ProtectedRoute><TrainerMealLibrary /></ProtectedRoute>} />
              <Route path="/trainer/exercises" element={<ProtectedRoute><TrainerExerciseLibrary /></ProtectedRoute>} />
              <Route path="/trainer/clients/:userId/plan" element={<ProtectedRoute><ClientPlanEditor /></ProtectedRoute>} />

              <Route path="/gym/dashboard" element={<ProtectedRoute><GymDashboard /></ProtectedRoute>} />
              <Route path="/gym/profile" element={<ProtectedRoute><GymProfile /></ProtectedRoute>} />
              <Route path="/gym/trainers" element={<ProtectedRoute><GymTrainers /></ProtectedRoute>} />
              <Route path="/gym/clients" element={<ProtectedRoute><GymClients /></ProtectedRoute>} />

              <Route path="/admin" element={<ProtectedRoute><AdminReviewQueue /></ProtectedRoute>} />
              <Route path="/admin/queue" element={<ProtectedRoute><AdminReviewQueue /></ProtectedRoute>} />
              <Route path="/admin/directory" element={<ProtectedRoute><AdminDirectory /></ProtectedRoute>} />
              <Route path="/admin/pricing" element={<ProtectedRoute><AdminPricing /></ProtectedRoute>} />

              <Route path="/marketplace" element={<ProtectedRoute><MarketplaceExplore /></ProtectedRoute>} />
              <Route path="/marketplace/gym/:gymId" element={<ProtectedRoute><MarketplaceGymProfile /></ProtectedRoute>} />
              <Route path="/marketplace/trainer/:trainerId" element={<ProtectedRoute><MarketplaceTrainerProfile /></ProtectedRoute>} />

              <Route path="/apply" element={<ProtectedRoute><BecomeVendor /></ProtectedRoute>} />
              <Route path="/apply/gym" element={<ProtectedRoute><GymApplication /></ProtectedRoute>} />
              <Route path="/apply/trainer" element={<ProtectedRoute><TrainerApplication /></ProtectedRoute>} />
              <Route path="/apply/under-review" element={<ProtectedRoute><UnderReview /></ProtectedRoute>} />
            </>
          )}
        </Routes>
      </Suspense>
      {showBottomNav && <BottomNav />}
    </>
  );
}

function App() {
  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ""}>
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </GoogleOAuthProvider>
  );
}

export default App;
